import Tracking from "../uidispatcherapi/Tracking";
import { SessionStore } from "../uidispatcherapi/SessionStore";
import { findAllFsdGroupHeaders, findAllNonFsdGroupHeaders } from "../model/CheckoutManipulation";
import type { DnrInfo, TotalPrices } from "../model/Prices";
import type { CheckoutData, CheckoutGroupHeader, CheckoutItem, FullPayment } from "../model/CheckoutData";

export const trackStructured: TrackingFunction = (
	eventType: string,
	structuredData?: any,
	hiddenData?: Record<string, any> | null | undefined,
) => {
	if ("function" === typeof Tracking.trackStructuredEvent) {
		const tags = {
			...structuredData?.tags,
			triggeredByEmployee: String(SessionStore.isEmployee()),
			pathname: window?.location?.pathname || "",
		};
		const enrichedData = { ...structuredData, tags: tags };
		Tracking.trackStructuredEvent("ordercapture", "ui", eventType, enrichedData, hiddenData);
	}
};
export function trackStructuredEvent(
	eventType: string,
	structuredData: any,
	hiddenData: Record<string, any> | null | undefined,
) {
	Tracking.trackStructuredEvent("ordercapture", "ui", eventType, structuredData, hiddenData);
}
export const trackUiError = (component: string, message: string, exception?: any): void => {
	if (!exception) {
		Tracking.trackError("ordercapture", "ui", component, message);
		console.warn("Error in component " + component + ": " + message);
	} else {
		Tracking.trackError("ordercapture", "ui", component, message, {
			exception: JSON.stringify(exception),
			stack: exception.stack,
		});
		console.warn("Error in component " + component + ": " + message, exception);
	}
};
export const trackUiInfo = (component: string, message: string) => {
	Tracking.trackStructuredEvent("ordercapture", "ui", "technical.client.info", {
		tags: {
			component,
			message,
		},
	});
};
export type TrackingArticle = {
	articleId: string;
	quantity: number;
	price: number | undefined;
};
export type TrackingFunction = (eventType: string, structuredData?: any) => void;
type TrackingPrices = {
	total: string;
	tax: string;
	shipping: string;
	articles: string;
};
export const trackOrderConfirmationData = (checkoutData: CheckoutData) => {
	const nonFsdHeaders = findAllNonFsdGroupHeaders(checkoutData);

	const orderAmount = nonFsdHeaders
		.map((header) => header?.groupPrices?.total?.basket?.articles?.total?.net?.amount ?? 0)
		.reduce((acc, val) => acc + val, 0);
	const orderTax = nonFsdHeaders
		.map((header) => header?.groupPrices?.total?.basket?.articles?.total?.vat?.amount ?? 0)
		.reduce((acc, val) => acc + val, 0);
	const shippingCosts = nonFsdHeaders
		.map((header) => header?.groupPrices?.total?.shipping?.total?.net?.amount ?? 0)
		.reduce((acc, val) => acc + val, 0);

	const tags = {
		stateOf: "submittedOrder",
	};
	const gtmTrackingData = {
		articles: mapArticleDataForAffiliateTracking(checkoutData.items, nonFsdHeaders),
		submitId: checkoutData.id,
		orderAmount: orderAmount,
		orderTax: orderTax,
		shippingCosts: shippingCosts,
	};
	Tracking.trackForGtmOnly(
		"ordercapture",
		"ui",
		"stateUpdate",
		{
			tags: tags,
		},
		gtmTrackingData,
	);
};
export const getSubmitSuccessTrackingTags = (checkoutData: CheckoutData, source: string) => {
	const orderType = getOrderType(checkoutData);
	const orderSource = checkoutData.header.orderSource;
	const trackingPrices = getTrackingPrices(checkoutData.checkoutPrices.total);
	const subHeaders = Object.keys(checkoutData.groupHeaders).join();
	const articleIds: string[] = Object.keys(checkoutData.items);

	const currency = checkoutData.checkoutPrices.total?.total?.net?.currency;

	const selectedServiceType = checkoutData.header.serviceTypeSelection.preferredServiceType;
	const locationId =
		selectedServiceType === "DELIVERY"
			? checkoutData.selectedDeliveryAddressId
			: checkoutData.selectedPickupStoreId;
	const usedSavedCard = locationId && checkoutData.header.paymentSelection?.selectedCreditCardInfo[locationId];

	const ownBrandArticles = Object.values(checkoutData.items).filter((item) => !!item.ownBrand);

	const ownBrandArticlesCount = ownBrandArticles.length;
	const ownBrandArticlesNetMoney = ownBrandArticles
		.map((item) => item.prices?.position.article.net?.amount)
		.filter((amount): amount is number => amount != null && amount != undefined)
		.reduce((a, b) => a + b, 0);
	const paymentSelection = checkoutData.header.paymentSelection;
	const mainPayment = paymentSelection?.mainPayment;
	const complementaryPayment = paymentSelection?.complementaryPayment;
	// TJ: This article data tag was added to the submit success event
	// for GTM tracking by Customer Inspiration (cf. story CIT-868)
	// Story for consolidation/clean up: BOC-61204
	const articleData = mapArticleDataForGtmTracking(Object.values(checkoutData.items));
	const articleCount = articleData.length;
	const shopChannel = mapToShopChannel(checkoutData);
	return {
		articleCount: articleCount,
		submitId: checkoutData.status.submitId,
		checkoutId: checkoutData.id,
		cartId: checkoutData.context.cartId,
		storeId: checkoutData.context.storeId,
		cartVersion: checkoutData.context.cartVersion + "",
		orderAmount: trackingPrices.total,
		orderTax: trackingPrices.tax,
		shippingCosts: trackingPrices.shipping,
		articlesCost: trackingPrices.articles,
		orderSource: String(orderSource),
		source: source,
		primaryPayment: getPaymentNames(mainPayment),
		secondaryPayment: getPaymentNames(complementaryPayment),
		subHeaders,
		orderType: orderType,
		articleIds: articleIds,
		usedSavedCard: usedSavedCard ? "true" : "false",
		currency: currency,
		blockedFromMetroCredit: checkoutData.customerData.blockedFromMetroCredit ? "true" : "false",
		ownBrandArticlesCount: ownBrandArticlesCount,
		ownBrandArticlesNetMoney: ownBrandArticlesNetMoney,
		articles: JSON.stringify(articleData),
		shopChannel: shopChannel,
	};
};

const getPaymentNames = (payment: FullPayment | null | undefined): string => {
	if (!payment) {
		return "";
	}

	const paymentType = payment.id.type;
	const paymentAllowance = payment.id.allowance;
	return paymentType ? `${paymentAllowance} - ${paymentType}` : paymentAllowance;
};

export const getArticlePrices = (total: TotalPrices | null | undefined) => {
	return total?.basket?.articles?.items?.net?.amount ?? "";
};

const getShippingAmount = (total: TotalPrices | null | undefined) => {
	return total?.shipping?.total?.net?.amount ?? "";
};

const getTrackingPrices = (total: TotalPrices | null | undefined): TrackingPrices => {
	const basket = total?.basket?.articles.total ?? null;
	const totalAmount = basket?.net?.amount ?? "";
	const taxAmount = basket?.vat?.amount ?? "";
	const articlePrices = getArticlePrices(total);
	const shippingAmount = getShippingAmount(total);
	return {
		total: totalAmount ? totalAmount.toString() : "",
		tax: taxAmount ? taxAmount.toString() : "",
		shipping: shippingAmount ? shippingAmount.toString() : "",
		articles: articlePrices ? articlePrices.toString() : "",
	};
};

const getOrderType = (checkoutData: CheckoutData): string => {
	const fsdHeaders = findAllFsdGroupHeaders(checkoutData);

	if (fsdHeaders.length === 0) {
		return "non-fsd";
	}

	if (fsdHeaders.length === Object.keys(checkoutData.groupHeaders).length) {
		return "fsd";
	}

	return "mixed";
};

const mapArticleDataForGtmTracking = (articles: Array<CheckoutItem>): Array<TrackingArticle> => {
	return articles
		.map((item) => {
			const price = item.prices?.bundle.sum.net?.amount;
			const priceWithPromotion = item.prices?.promotionPrice?.amount ?? price;

			return {
				articleId: item.bundleId,
				quantity: item.quantity,
				price: priceWithPromotion,
			};
		})
		.sort((item1, item2) => item1.articleId.localeCompare(item2.articleId));
};

const mapArticleDataForAffiliateTracking = (
	items: Record<string, CheckoutItem>,
	headers: Array<CheckoutGroupHeader>,
): Array<TrackingArticle> => {
	// create an index structure for faster testing if an article has the correct headerReference
	const headerIdLookup = new Set(headers.map((header) => header.headerId));
	return Object.values(items)
		.map((item) => {
			if (!headerIdLookup.has(item.headerReference.value)) {
				return null;
			}

			const price = item.prices?.bundle.sum.net?.amount;
			const priceWithPromotion = item.prices?.promotionPrice?.amount ?? price;

			return {
				articleId: item.bundleId,
				quantity: item.quantity,
				price: priceWithPromotion,
			};
		})
		.filter((item): item is TrackingArticle => !!item)
		.sort((item1, item2) => item1.articleId.localeCompare(item2.articleId));
};

const lastTrackedCartItems: Array<TrackingArticle> = [];

const haveCartArticlesChanged = (items: Array<TrackingArticle>) => {
	if (items.length !== lastTrackedCartItems.length) {
		return true;
	}

	for (let i = 0; i < items.length; i++) {
		if (lastTrackedCartItems[i].articleId !== items[i].articleId) {
			return true;
		}

		if (lastTrackedCartItems[i].quantity !== items[i].quantity) {
			return true;
		}
	}

	return false;
};

export function mapToShopChannel(checkoutData: CheckoutData | null | undefined): string | null | undefined {
	return Tracking.mapToShopChannel(extractDistinctFulfillmentTypes(checkoutData));
}
export function extractDistinctFulfillmentTypes(
	checkoutData: CheckoutData | null | undefined,
): Array<string> | null | undefined {
	if (!checkoutData) return null;
	const allFulfillmentTypes = Object.values(checkoutData.groupHeaders).map((header) => header?.fulfillmentType);
	const filterFTs = allFulfillmentTypes.filter(Boolean) as string[];
	return [...new Set(filterFTs)];
}
export function transformDnrInfoArrayToConcattedString(dnrInfoArray: DnrInfo[]): string {
	return dnrInfoArray.map((dnrInfo) => dnrInfo.name).join("|");
}
export default {
	trackStructured,
	trackStructuredEvent,
	trackUiError,
	trackUiInfo,
	mapArticleDataForTracking: mapArticleDataForGtmTracking,
	haveCartArticlesChanged,
	getSubmitSuccessTrackingTags,
	trackOrderConfirmationData,
};
export const mapArticleDataForTracking = mapArticleDataForGtmTracking;
